import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePractice } from '../contexts/PracticeContext';
import ImportBatchDialog from '../components/lab/ImportBatchDialog';
import OrganizationEmptyState from '../components/OrganizationEmptyState';

const mockResultBatches = [
  { batchId: 'BATCH-7001', completionDate: '2026-02-25', active: true, breakdown: '35 normal, 6 pending' },
  { batchId: 'BATCH-7002', completionDate: '2026-02-24', active: false, breakdown: '29 normal, 4 pending, 2 flagged' },
  { batchId: 'BATCH-7003', completionDate: '2026-02-23', active: true, breakdown: '41 normal, 3 pending' },
  { batchId: 'BATCH-7004', completionDate: '2026-02-22', active: false, breakdown: '22 normal, 7 pending, 1 flagged' },
  { batchId: 'BATCH-7005', completionDate: '2026-02-21', active: true, breakdown: '38 normal, 5 pending' },
  { batchId: 'BATCH-7006', completionDate: '2026-02-20', active: true, breakdown: '31 normal, 4 pending' },
  { batchId: 'BATCH-7007', completionDate: '2026-02-19', active: false, breakdown: '19 normal, 9 pending, 3 flagged' },
  { batchId: 'BATCH-7008', completionDate: '2026-02-18', active: true, breakdown: '44 normal, 2 pending' },
  { batchId: 'BATCH-7009', completionDate: '2026-02-17', active: false, breakdown: '27 normal, 8 pending' },
  { batchId: 'BATCH-7010', completionDate: '2026-02-16', active: true, breakdown: '33 normal, 3 pending, 1 flagged' },
  { batchId: 'BATCH-7011', completionDate: '2026-02-15', active: true, breakdown: '30 normal, 5 pending' },
  { batchId: 'BATCH-7012', completionDate: '2026-02-14', active: false, breakdown: '25 normal, 6 pending, 2 flagged' },
];

export default function LabResultBatches() {
  const navigate = useNavigate();
  const { selectedPractice } = usePractice();
  const [searchText, setSearchText] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [importBatchOpen, setImportBatchOpen] = React.useState(false);

  React.useEffect(() => {
    setPage(0);
  }, [searchText, activeFilter]);

  const filteredRows = mockResultBatches.filter((batch) => {
    const matchesSearch =
      searchText.trim() === '' ||
      `${batch.batchId} ${batch.breakdown}`.toLowerCase().includes(searchText.trim().toLowerCase());
    const matchesActive =
      activeFilter === 'all' ||
      (activeFilter === 'active' && batch.active) ||
      (activeFilter === 'inactive' && !batch.active);
    return matchesSearch && matchesActive;
  });
  const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!selectedPractice) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a lab to view result batchs.
        </Typography>
        <OrganizationEmptyState label="lab" />
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" component="h1">
                Result Batchs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Mock batch imports for {selectedPractice.name}.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setImportBatchOpen(true)}
              sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}
            >
              Import Batch
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label="Search Batch / Breakdown"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Active"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Batch ID</TableCell>
                <TableCell>Completion Date</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Breakdown</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((batch) => (
                <TableRow
                  key={batch.batchId}
                  hover
                  onClick={() => navigate(`/lab-result-batchs/${batch.batchId}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{batch.batchId}</TableCell>
                  <TableCell>{batch.completionDate}</TableCell>
                  <TableCell>{batch.active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{batch.breakdown}</TableCell>
                </TableRow>
              ))}
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No result batchs found for current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </CardContent>
      </Card>

      <ImportBatchDialog open={importBatchOpen} onClose={() => setImportBatchOpen(false)} />
    </Container>
  );
}
